import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type { MessageType } from 'garaje/services/flash-messages';
import type LocalStorageService from 'garaje/services/local-storage';
import type MessageBusService from 'garaje/services/message-bus';
import type StateService from 'garaje/services/state';
import urlBuilder from 'garaje/utils/url-builder';

interface EmbeddedAppMessage {
  event: string;
  type?: MessageType;
  msg?: string;
  query?: {
    data?: unknown;
  };
}

export default class LocationOverviewOccupancySettingsController extends Controller {
  @service declare localStorage: LocalStorageService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare messageBus: MessageBusService;

  @tracked loginRequired = false;
  @tracked showModal = false;
  @tracked modalIframeUrl: string | undefined;
  @tracked modalData = {};
  @tracked showOccupancyCsvUploadDeleteModal = false;
  @tracked showOccupancyCsvUploadResultsModal = false;
  @tracked csvUploadData: unknown;

  constructor(properties: Record<string, unknown>) {
    super(properties);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  get iframeUrl(): string {
    const companyId = this.state.currentCompany.id;
    return urlBuilder.embeddedSettingsGlobalAnalyticsUrl(companyId, this.loginRequired);
  }

  handleShowToaster(message: EmbeddedAppMessage): void {
    const type = message.type;
    const msg = message.msg;
    if (type && msg) {
      this.flashMessages.showAndHideFlash(type, msg);
    }
  }

  handleShowOccupancyCsvUploadDeleteModal(message: EmbeddedAppMessage, companyId: string): void {
    const data = message?.query?.data;
    this.csvUploadData = data;
    this.modalIframeUrl = urlBuilder.embeddedOccupancyCsvUploadDeleteModal(companyId, this.loginRequired);
    this.showOccupancyCsvUploadDeleteModal = true;
  }

  handleCloseOccupancyCsvUploadDeleteModal(): void {
    this.showOccupancyCsvUploadDeleteModal = false;
  }

  handleShowOccupancyCsvUploadResultsModal(message: EmbeddedAppMessage, companyId: string): void {
    const data = message?.query?.data;
    this.csvUploadData = data;
    this.modalIframeUrl = urlBuilder.embeddedOccupancyCsvUploadResultsModal(companyId, this.loginRequired);
    this.showOccupancyCsvUploadResultsModal = true;
  }

  handleCloseOccupancyCsvUploadResultsModal(): void {
    this.showOccupancyCsvUploadResultsModal = false;
  }

  @action
  handleMessage(message: EmbeddedAppMessage): void {
    const {
      currentCompany: { id: companyId },
    } = this.state;

    switch (message.event) {
      case 'showModal':
        this.modalData = message?.query?.data || {};
        this.modalIframeUrl = urlBuilder.analytics.settingsHrisUploadModalGlobalUrl(companyId, this.loginRequired);
        this.showModal = true;
        break;
      case 'closeModal':
        this.showModal = false;
        break;
      case 'showToaster':
        this.handleShowToaster(message);
        break;
      case 'showOccupancyCsvUploadDeleteModal':
        this.handleShowOccupancyCsvUploadDeleteModal(message, companyId);
        break;
      case 'closeOccupancyCsvUploadDeleteModal':
        this.handleCloseOccupancyCsvUploadDeleteModal();
        break;
      case 'showOccupancyCsvUploadViewResults':
        this.handleShowOccupancyCsvUploadResultsModal(message, companyId);
        break;
      case 'closeOccupancyCsvUploadResultsModal':
        this.handleCloseOccupancyCsvUploadResultsModal();
        break;
      default:
        break;
    }
  }

  @action
  trackLoggedIn(): void {
    /**
     * Issue: When user logs out from the Garaje app there is no way Next.js app knows it also needs to logout.
     * Due to this the cookies of the logout user may persist.
     * Solution: After login, force login the user on his first visit to this app. This refreshes all the cookies
     * To know if the user is visiting for the first time we will maintain a global state in localstorage called 'wa_logged_in'
     * On Login -> wa_logged_in does not exists in localstorage -> its first visit
     * On first visit -> SignIn, set wa_logged_in = true in localstorage
     * On Logout -> Remove wa_logged_in
     */

    const waLoggedIn = this.localStorage.getItem('wa_logged_in');

    if (!waLoggedIn) {
      this.loginRequired = true;
      this.localStorage.setItem('wa_logged_in', true);
    } else {
      this.loginRequired = false;
    }
  }
}
